import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import * as storage from 'storage/recentSearches/storage';
import { RecentSearch } from 'storage/recentSearches/common';
import * as accountSelectors from 'store/account/selectors';
import * as geolocationSelectors from 'store/geolocation/selectors';
import { ProviderFacetKeys } from 'store/providerSearch/types';
import * as providerSearchSelectors from 'store/providerSearch/selectors';
import { quickLinks, mobileLinks } from './QuickLinks';
import { QuickLink } from './types';
import { sortAlphabeticDescending } from './QuickLinks/utils';

//
//  Hook: useProviderSearchQuickLinks
//
//  Description:
//  Handles quick links data for provider search
//

export function useProviderSearchQuickLinks(isMobile = false) {
  const consumerId = useSelector(accountSelectors.currentAccountConsumerIdSelector);
  const geolocationAccepted = useSelector(geolocationSelectors.geolocationAcceptedSelector);
  const specialtyKey = ProviderFacetKeys.specialty;
  const specialties = useSelector(providerSearchSelectors.facetsDataSelector)?.[specialtyKey];
  const displayLinks = isMobile ? mobileLinks : quickLinks;

  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const [links, setLinks] = useState<Mutable<ReturnType<typeof displayLinks>> | null>(null);

  const sortedSpecialties = specialties != null ? sortAlphabeticDescending(specialties) : null;

  //
  //  Method: init
  //  Description: Initializes quick links setup built off the quickLinks structure constant
  //
  const init = async () => {
    const clone: Mutable<ReturnType<typeof quickLinks>> = cloneDeep(
      displayLinks === quickLinks ? quickLinks(setLinks) : mobileLinks()
    );

    if (!geolocationAccepted) {
      delete clone.nearMeSearch;
    }

    if (!consumerId) {
      delete clone.recentSearches;
    }
    setLinks(clone);
    setLoading(true);

    const recent = await storage.getAllRecentSearches(consumerId);
    if (isMobile) {
      setRecentSearches(Array.isArray(recent) ? recent.slice(0, 3) : []);
    } else {
      setRecentSearches(Array.isArray(recent) ? recent : []);
    }
    setLinks(clone);
    setLoading(false);
  };

  //
  //  Method: empty
  //  Description: Resets the quick links to its initial value
  //
  const empty = () => {
    setLinks(null);
  };

  //
  //  Method: generateLinks
  //  Description: Generates the structure of the quick links data
  //
  const generateLinks = () => {
    if (!links) return null;
    let result: QuickLink[] = [];
    if (isMobile) {
      const recentSearchesResults = links?.recentSearches
        ? links.recentSearches(recentSearches)
        : null;
      result = Object.values({
        ...links,
        ...recentSearchesResults,
        ...sortedSpecialties
      }) as QuickLink[];
      return result;
    }
    if (links && links.recentSearches) {
      result = Object.values({
        ...links,
        recentSearches: links.recentSearches(recentSearches, loading)
      }) as QuickLink[];
    } else {
      result = Object.values(links) as QuickLink[];
    }
    return result;
  };

  useEffect(() => {
    init();
  }, []);

  return {
    init,
    empty,
    data: generateLinks()
  };
}
